import { LoadingOutlined } from "@ant-design/icons";
import { Button, Form, Input, InputNumber, Modal, Select, Spin } from "antd";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { GetApproversApi } from "../../api/approvals/GetApproversApi";
import { openNotificationError } from "../../helpers/NotificationHelpers";
import { getTargetString } from "../../helpers/targetHelpers";

const { Option } = Select;

function UpdateTargetProgressModal(props) {
  const positionState = useSelector((state) => state.positionState.value);
  const [approvers, setApprovers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [valuesChanged, setValuesChanged] = useState(false);
  const [form] = Form.useForm();

  // Check if this plan or any ancestor has percentage updates enabled
  const isPercentageUpdate = Boolean(
    // Direct plan setting - be more explicit about checking
    (props.data?.isPercentageUpdate === true || 
     props.data?.isPercentageUpdate === 1 || 
     props.data?.isPercentageUpdate === "1" ||
     props.data?.isPercentageUpdate === "true") ||
    // Ancestor inheritance for cascade plans
    (props.data?.ancestorHasPercentageUpdate === true)
  );

  // Check if this is a negative scale plan
  const isNegativeScale = Boolean(
    props.data?.isNegativeScale === true || 
    props.data?.isNegativeScale === 1 || 
    props.data?.isNegativeScale === "1" ||
    props.data?.isNegativeScale === "true"
  );

  useEffect(() => {
    getApprovers();
  }, []);

  function handleCancel() {
    setValuesChanged(false);
    props.handleCancel();
    form.resetFields();
  }

  async function getApprovers() {
    const apiResponse = await GetApproversApi(positionState?.id);

    if (apiResponse.error) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      setApprovers(apiResponse?.data);
      form.setFields([
        {
          name: "approver",
          value: apiResponse?.data[0]?.id || "",
        },
      ]);
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't edit approver. Try again later."
      );
    }
    setLoading(false);
  }

  return (
    <Modal
      title="Update Target Progress"
      visible={props.visible}
      onCancel={() => handleCancel()}
      footer={null}
      destroyOnClose={true}
    >
      <>
        {loading ? (
          <div className="flex justify-center items-center h-20">
            <Spin />
          </div>
        ) : (
          <Form
            name="planGroup"
            onFinish={props.onFinish}
            autoComplete="off"
            layout="vertical"
            onValuesChange={() => {
              setValuesChanged(true);
            }}
            form={form}
            requiredMark={false}
          >
            <div className="flex items-center">
              <Form.Item
                label={isPercentageUpdate ? 
                  (isNegativeScale ? "Remaining Percentage: " : "Progress Percentage: ") : 
                  "Target Progress: "
                }
                name="target"
                initialValue={isPercentageUpdate ? 
                  // For percentage mode, calculate current progress percentage
                  (() => {
                    if (isNegativeScale) {
                      // NEGATIVE SCALE: Show remaining percentage (100% - achieved percentage)
                      const startingValue = Math.abs(Number(props.data?.target) || 0);
                      const achievedReduction = props.targetProgress || 0;
                      if (startingValue > 0) {
                        const achievedPercentage = (achievedReduction / startingValue) * 100;
                        const remainingPercentage = 100 - achievedPercentage;
                        return Math.round(Math.max(remainingPercentage, 0));
                      }
                      return 100;
                    } else {
                      // NORMAL SCALE: Show achieved percentage
                      return Math.round(((props.targetProgress || 0) / (Number(props.data?.target) || 1)) * 100);
                    }
                  })() :
                  props.targetProgress || 0
                }
                rules={[
                  {
                    required: true,
                    message: isPercentageUpdate ? "Please enter progress percentage!" : "Please enter target!",
                  },
                  ...(isPercentageUpdate ? [{
                    type: 'number',
                    min: 0,
                    max: 100,
                    message: 'Progress percentage must be between 0 and 100',
                  }] : [])
                ]}
              >
                <InputNumber 
                  addonAfter={isPercentageUpdate ? "%" : null}
                  min={0}
                  max={isPercentageUpdate ? 100 : undefined}
                  step={isPercentageUpdate ? 0.01 : 1}
                  precision={isPercentageUpdate ? 2 : 0}
                  placeholder={isPercentageUpdate ? 
                    (isNegativeScale ? "Enter remaining percentage (0-100)" : "Enter percentage (0-100)") : 
                    "Enter target value"
                  }
                  formatter={(value) => {
                    if (!value) return '';
                    if (isPercentageUpdate) {
                      // For percentage mode, don't add commas
                      return value.toString();
                    } else {
                      // For target value mode, add commas for better readability
                      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                    }
                  }}
                  parser={(value) => {
                    if (!value) return '';
                    // Remove commas when parsing
                    return value.replace(/,/g, '');
                  }}
                  onChange={(value) => {
                    // Input change handler
                  }}
                />
              </Form.Item>
              {isPercentageUpdate ? (
                <div className="ml-3 text-lg">
                  Target: {getTargetString(
                    props.data?.target,
                    props.data?.scale?.scaleName,
                    props.data?.scale?.scaleUnitAbbreviation
                  )}
                </div>
              ) : (
                <>
                  <div className="mx-1 text-lg">/</div>
                  <div className="text-lg">
                    {getTargetString(
                      props.data?.target,
                      props.data?.scale?.scaleName,
                      props.data?.scale?.scaleUnitAbbreviation
                    )}
                  </div>
                </>
              )}
            </div>
            {(positionState?.userRole?.name === "Manager" ||
              positionState?.userRole?.name === "User") &&
              !!approvers?.length && (
                <Form.Item
                  label="Approver: "
                  name="approver"
                  rules={[
                    {
                      required:
                        (positionState?.userRole?.name === "Manager" ||
                          positionState?.userRole?.name === "User") &&
                        !!approvers?.length &&
                        true,
                      message: "Please select approver!",
                    },
                  ]}
                >
                  <Select
                    allowClear
                    showSearch
                    placeholder="Select approver"
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    filterSort={(optionA, optionB) =>
                      optionA.children
                        .toLowerCase()
                        .localeCompare(optionB.children.toLowerCase())
                    }
                  >
                    {approvers?.map((approver) => {
                      return (
                        <Option key={approver?.id} value={approver?.id}>
                          {(approver?.designations?.abbreviation ||
                            approver?.designations?.name) +
                            "," +
                            approver?.units?.name}
                        </Option>
                      );
                    })}
                  </Select>
                </Form.Item>
              )}

            <Form.Item style={{ marginBottom: 0 }}>
              <div className=" flex justify-end">
                <Button className="mr-3 w-28" onClick={() => handleCancel()}>
                  Cancel
                </Button>
                <Button
                  className="btn w-28"
                  type="primary"
                  htmlType="submit"
                  disabled={props.submit || !valuesChanged}
                  onClick={async () => {
                    const values = await form.validateFields();
                    
                    // CRITICAL FIX: Calculate actual target for percentage-based plans
                    let actualTarget = values.target;
                    
                    if (isPercentageUpdate) {
                      // Convert percentage to actual amount
                      const planTargetRaw = props.data?.target;
                      const planTargetString = String(planTargetRaw || '0');
                      const planTargetCleaned = planTargetString.replace(/[^0-9.-]/g, ''); // Remove any non-numeric characters except decimal and minus
                      const planTargetNumber = Number(planTargetCleaned) || 0;
                      const userPercentage = Number(values.target) || 0;
                      
                      // SAFETY CHECK: Ensure target is reasonable (not already calculated)
                      if (planTargetNumber > 1000000000000) { // If target is over 1 trillion, something is wrong
                        actualTarget = userPercentage; // Fallback to just the percentage value
                      } else {
                        if (isNegativeScale) {
                          // NEGATIVE SCALE LOGIC:
                          // User enters remaining percentage (e.g., 75% remaining)
                          // We need to calculate achieved reduction
                          // If 75% remaining, then 25% achieved
                          const achievedPercentage = 100 - userPercentage;
                          actualTarget = (achievedPercentage / 100) * planTargetNumber;
                        } else {
                          // NORMAL SCALE LOGIC:
                          // User enters achieved percentage (e.g., 75% achieved)
                          actualTarget = (userPercentage / 100) * planTargetNumber;
                        }
                      }
                    }
                    
                    // Call the onFinish with the calculated target
                    const modifiedValues = { ...values, target: actualTarget };
                    props.onFinish(modifiedValues);
                  }}
                >
                  {props.submit ? (
                    <Spin
                      size="small"
                      indicator={
                        <LoadingOutlined style={{ color: "#fff" }} spin />
                      }
                    />
                  ) : (
                    "Save"
                  )}
                </Button>
              </div>
            </Form.Item>
          </Form>
        )}
      </>
    </Modal>
  );
}
export default UpdateTargetProgressModal;
